import { useCallback, useLayoutEffect, useRef, useState, type RefObject } from "react";

import { useBoardViewportTouchGestures } from "./boardTouchGestures";
import type { BoardPinchZoomUpdate } from "./boardTouchScroll";
import type { BoardTouchTap } from "./boardTouchTapTracking";
import { MIN_ZOOM } from "./boardViewport";

export function useBoardPinchZoom({
  viewportRef,
  canvasRef,
  enabled,
  onTap,
}: {
  viewportRef: RefObject<HTMLDivElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  enabled: boolean;
  onTap?: (tap: BoardTouchTap) => void;
}): number {
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const pendingScrollRef = useRef<{ scrollLeft: number; windowScrollY: number } | null>(null);

  const getZoom = useCallback(() => zoomRef.current, []);
  const onZoom = useCallback((update: BoardPinchZoomUpdate) => {
    pendingScrollRef.current = {
      scrollLeft: update.scrollLeft,
      windowScrollY: update.windowScrollY,
    };
    setZoom(update.zoom);
  }, []);

  useBoardViewportTouchGestures({ viewportRef, canvasRef, enabled, getZoom, onTap, onZoom });

  useLayoutEffect(() => {
    const pending = pendingScrollRef.current;
    if (!pending) return;
    pendingScrollRef.current = null;
    const viewport = viewportRef.current;
    if (viewport) viewport.scrollLeft = pending.scrollLeft;
    window.scrollTo({ top: pending.windowScrollY, behavior: "auto" });
  }, [zoom, viewportRef]);

  return enabled ? zoom : 1;
}
